import { POPOVER_CLASS } from './driverTheme';

// driver.js keeps its state at module scope, not per instance: one config, one
// popover element, one set of body classes shared by every `driver()` call. Two
// components constructing their own instances therefore fight — whichever runs
// second overwrites the first's config, and either one's destroy() tears down
// the other mid-flight. This module is the single owner: one live instance at a
// time, and claiming it releases whoever held it before.

type Driver = ReturnType<typeof import('driver.js').driver>;
type DriverConfig = Parameters<typeof import('driver.js').driver>[0];

// Shared look for both overlays. They had drifted to different padding, radius
// and dim levels for no design reason.
export const DRIVER_BASE = {
  allowClose: true,
  stagePadding: 8,
  stageRadius: 12,
  overlayOpacity: 0.6,
  // No step-to-step animation. driver.js animates the stage between elements
  // and, when that animation ends, re-places the popover against the element
  // it was moving FROM — the card visibly landed beside the right control and
  // then hopped back to the previous one. Without the animation each stop is
  // placed once, where it belongs.
  animate: false,
  popoverClass: POPOVER_CLASS
} as const;

let loader: Promise<typeof import('driver.js')> | null = null;

// One dynamic import for the whole feature, memoized so a second caller reuses
// the same chunk instead of racing a second network request.
const loadDriver = () => {
  if (!loader) loader = Promise.all([import('driver.js'), import('driver.js/dist/driver.css')]).then(([mod]) => mod);
  return loader;
};

let active: { owner: symbol; instance: Driver } | null = null;

/** Whoever is holding the driver right now, if anyone. */
export const currentOwner = () => active?.owner;

/**
 * Take exclusive control of the driver. Any previous holder is destroyed first,
 * so there is never more than one instance touching driver.js' shared state.
 * Returns null if the caller was superseded while the chunk was loading.
 */
export const claimDriver = async (owner: symbol, config: DriverConfig): Promise<Driver | null> => {
  const { driver } = await loadDriver();
  releaseDriver(active?.owner);
  const instance = driver({ ...DRIVER_BASE, ...config });
  active = { owner, instance };
  return active.owner === owner ? instance : null;
};

/** Release only if `owner` still holds it — a late cleanup must not kill a newer overlay. */
export const releaseDriver = (owner: symbol | undefined) => {
  if (!owner || active?.owner !== owner) return;
  const { instance } = active;
  active = null;
  instance.destroy();
};

/**
 * driver.js places a popover once, against the rect it measured as the step
 * began, and re-placing it ANIMATES — so a blanket refresh makes the card
 * dance. Worse, `.driver-active-element` is not reliable mid-sequence (it can
 * still sit on the previous stop's node), and a refresh issued right after a
 * step change re-places against driver's stale element — which is what put the
 * "Complete purchase" card back on the card picker.
 *
 * So: watch the element WE asked driver to highlight, stay quiet while the
 * step animates in, and refresh only when that element has really moved. A
 * card placed correctly is never touched; one whose target slides away (a late
 * invoice preview, a reprinted ticket row) follows it.
 */
const SETTLE_MS = 420; // driver's own move-in; refreshing inside it is what danced
const MOVED_PX = 2;

export interface HighlightTracker {
  /** Call from onHighlightStarted with the element this stop points at. */
  step: (el: Element | null | undefined) => void;
  dispose: () => void;
}

export const trackHighlighted = (instance: Driver): HighlightTracker => {
  let target: Element | null = null;
  let base: DOMRect | null = null;
  let quietUntil = 0;
  let raf = 0;
  const check = () => {
    raf = 0;
    if (active?.instance !== instance || !instance.isActive() || !target || !target.isConnected) return;
    const r = target.getBoundingClientRect();
    if (!r.width && !r.height) return;
    if (performance.now() < quietUntil || !base) {
      base = r;
      return;
    }
    const moved =
      Math.abs(r.top - base.top) > MOVED_PX ||
      Math.abs(r.left - base.left) > MOVED_PX ||
      Math.abs(r.width - base.width) > MOVED_PX ||
      Math.abs(r.height - base.height) > MOVED_PX;
    if (!moved) return;
    base = r;
    // refresh() re-places against driver's OWN idea of the current element,
    // which lags a step change — refreshing then threw the card back onto the
    // previous control. Only correct when driver agrees the target is current.
    if (!target.classList.contains('driver-active-element')) return;
    instance.refresh();
  };
  const bump = () => {
    if (raf || typeof window === 'undefined') return;
    raf = window.requestAnimationFrame(check);
  };
  const mo =
    typeof MutationObserver === 'undefined'
      ? null
      : new MutationObserver((records) => {
          // Driver's own DOM churns constantly; only real page changes matter.
          if (records.some((r) => !(r.target instanceof Element) || !r.target.closest('[class*="driver-"]'))) bump();
        });
  mo?.observe(document.body, { childList: true, subtree: true, characterData: true });
  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(bump) : null;
  ro?.observe(document.body);
  window.addEventListener('scroll', bump, true);
  window.addEventListener('resize', bump);
  const poll = window.setInterval(bump, 250);
  return {
    step: (el) => {
      target = el ?? null;
      base = null;
      quietUntil = performance.now() + SETTLE_MS;
    },
    dispose: () => {
      mo?.disconnect();
      ro?.disconnect();
      window.removeEventListener('scroll', bump, true);
      window.removeEventListener('resize', bump);
      window.clearInterval(poll);
      if (raf) window.cancelAnimationFrame(raf);
      target = null;
    }
  };
};
